<template>
    <div>
        <photography-delivery-shell
            v-if="!creator"
            :service-order-no="serviceOrderNo"
            :title="workspaceTitle"
            :previewing="!!active"
            @back="backToOrders"
        >
            <main class="retouch-workspace">
                <p class="subtitle">查看完整精修版本，逐张告诉摄影师需要调整的地方。</p>
                <p v-if="loading" role="status">正在读取正式交付范围…</p>
                <p v-if="error" role="alert">
                    {{ error }} <button @click="initialize">重新加载正式结果及授权</button>
                </p>
                <button class="refresh-progress" :disabled="writeFrozen" @click="initialize">
                    刷新正式进度
                </button>
                <div class="stage-facts">
                    <span>第 {{ currentVersionNo }} 版 · 最多两轮返修，三版交付</span
                    ><span v-if="deadlineAt"
                        >{{ paused ? '暂停前阶段截止' : '有效阶段截止' }}：{{ deadlineAt }}</span
                    ><span v-if="[4, 6].includes(stage)"
                        >正式发布后七个自然日内确认或反馈，以服务端期限为准</span
                    ><span v-if="paused"
                        >摄影争议暂停 · 暂停开始：{{ pausedAt }}；解除后继续剩余有效时间</span
                    ><span v-else-if="deadlineExpired">本阶段期限已截止，请刷新最新处理状态</span>
                </div>
                <p v-if="feedbackState.result" class="notice" role="status">
                    本轮意见已正式提交，整轮已锁定，等待摄影师返修。
                </p>
                <p v-else-if="[5, 7].includes(stage)" class="notice" role="status">
                    本轮正式意见已锁定，等待摄影师交付下一完整版本。
                </p>
                <section v-if="stage === 8" class="completion">
                    <img src="@/assets/photography-delivery/3b044.svg" alt="" />
                    <div>
                        <h2>精修已完成，领取属于你的照片</h2>
                        <p>当前完整最终成片已由服务端确认。最终完成时间：{{ finalCompletedAt }}</p>
                        <router-link
                            class="primary"
                            :to="{ name: 'photographyDownloads', params: { serviceOrderNo } }"
                            >领取最终 JPG 成片</router-link
                        >
                    </div>
                </section>
                <section v-if="stage === 8" class="completed-review-context"><div><h2>服务评价</h2><p>分享本次服务体验，帮助我们为您更好地服务。是否可评价以预约内各项服务的最新状态为准。</p></div><router-link :to="{ name: 'photographyAppointments' }">查看预约与评价</router-link></section>
                <div
                    v-if="feedbackStep === 'editing' || feedbackStep === 'review'"
                    class="workspace-columns"
                >
                    <section>
                        <div class="feedback-cards">
                            <article
                                v-for="item in feedbackPhotos"
                                :key="item.retouchImageId"
                                class="photo-note card"
                            >
                                <header>
                                    <div>
                                        <strong>照片 {{ photoLabel(item.photoNo) }}</strong
                                        ><small
                                            >精修照片 · 来源第 {{ item.sourceVersionNo }} 版</small
                                        >
                                    </div>
                                    <button @click="preview(item)">预览 ↗</button>
                                </header>
                                <img
                                    v-if="item.url"
                                    :src="item.url"
                                    :alt="`精修#${item.photoNo}`"
                                    @error="imageFailed(item)"
                                />
                                <p v-else class="image-missing">
                                    {{ item.error || '正在读取授权照片' }}
                                    <button @click="access(item, 1)">重新读取</button>
                                </p>
                                <label
                                    >返修备注<textarea
                                        v-if="feedbackStep === 'editing'"
                                        :value="opinionValue(item)"
                                        :disabled="feedbackState.busy || feedbackState.unknown"
                                        placeholder="请具体描述需要调整的地方，每张最多200个Unicode字符"
                                        @input="editFeedback(item, $event.target.value)"
                                    ></textarea>
                                    <p v-else class="review-note">
                                        {{ opinionValue(item).trim() }}
                                    </p></label
                                ><small
                                    >{{ Array.from(opinionValue(item).trim()).length }}/200</small
                                >
                                <p class="filled">
                                    <img
                                        v-if="opinionValue(item).trim()"
                                        src="@/assets/photography-delivery/290d9.svg"
                                        alt=""
                                    />{{
                                        opinionValue(item).trim()
                                            ? '备注已填写'
                                            : '请填写非空返修意见'
                                    }}
                                </p>
                            </article>
                        </div>
                        <p class="notice">
                            <img
                                src="@/assets/photography-delivery/71027.svg"
                                alt=""
                            />可从肤色、光线、细节等方面描述返修需求，每张照片最多 200 个 Unicode
                            字符。
                        </p>
                    </section>
                    <aside class="card summary">
                        <h2>{{ feedbackStep === 'review' ? '核对本轮返修' : '发送摘要' }}</h2>
                        <span class="badge"
                            ><img src="@/assets/photography-delivery/13942.svg" alt="" />{{
                                feedbackPhotos.length
                            }}
                            张照片 · {{ feedbackState.items.length }} 条备注</span
                        >
                        <p v-for="item in feedbackPhotos" :key="item.retouchImageId">
                            {{ photoLabel(item.photoNo) }} ·
                            {{ opinionValue(item).trim() || '尚未填写' }}
                        </p>
                        <p>照片与备注将一同发送给摄影师，正式提交后整轮锁定，不能追加或撤回。</p>
                        <button
                            v-if="feedbackStep === 'editing'"
                            class="primary"
                            @click="reviewFeedback"
                        >
                            核对本轮返修 →</button
                        ><button
                            v-else
                            class="primary"
                            :disabled="
                                feedbackState.busy || confirmation.busy || confirmation.unknown
                            "
                            @click="submitReviewedFeedback"
                        >
                            {{
                                feedbackState.unknown ? '用原反馈请求核验结果' : '确认发送本轮意见'
                            }}</button
                        ><button
                            :disabled="feedbackState.busy || feedbackState.unknown"
                            @click="
                                feedbackStep = feedbackStep === 'review' ? 'editing' : 'selecting'
                            "
                        >
                            {{ feedbackStep === 'review' ? '返回调整' : '返回选片' }}
                        </button>
                        <p v-if="feedbackState.error" role="alert">{{ feedbackState.error }}</p>
                    </aside>
                </div>
                <div v-else class="workspace-columns">
                    <section class="card version-gallery">
                        <header class="gallery-toolbar">
                            <label
                                >查看已发布版本
                                <select
                                    v-model.number="viewVersionNo"
                                    :disabled="writeFrozen"
                                    @change="load(1)"
                                >
                                    <option
                                        v-for="version in currentVersionNo"
                                        :key="version"
                                        :value="version"
                                    >
                                        第 {{ version }} 版{{
                                            version === currentVersionNo
                                                ? '（当前完整版本）'
                                                : '（历史）'
                                        }}
                                    </option>
                                </select></label
                            ><span>全部 {{ total }} 张</span>
                        </header>
                        <p v-if="viewVersionNo !== currentVersionNo" class="notice">
                            当前正在查看历史完整版本，仅供查阅。
                        </p>
                        <p v-if="feedbackStep === 'selecting'">
                            勾选需要返修的照片，逐张填写调整需求。
                        </p>
                        <div class="retouch-grid">
                            <article
                                v-for="item in items"
                                :key="item.retouchImageId"
                                :class="['retouch-photo', { selected: isFeedbackPhoto(item) }]"
                            >
                                <button class="photo-open" @click="preview(item)">
                                    <img
                                        v-if="item.url"
                                        :src="item.url"
                                        :alt="`精修#${item.photoNo}`"
                                        @error="imageFailed(item)"
                                    /><span v-else class="image-missing">{{
                                        item.error || '照片读取中'
                                    }}</span></button
                                ><button
                                    v-if="feedbackStep === 'selecting' && canFeedback"
                                    class="select-photo"
                                    :aria-label="`返修照片 #${item.photoNo}`"
                                    :aria-pressed="isFeedbackPhoto(item)"
                                    @click="toggleFeedbackPhoto(item)"
                                >
                                    <img v-if="isFeedbackPhoto(item)" src="@/assets/photography-delivery/5448b.svg" alt=""><span v-else class="empty-photo-check"></span><span class="sr-only">返修照片 #{{ item.photoNo }}</span>
                                </button>
                                <footer>
                                    <strong>{{ photoLabel(item.photoNo) }}</strong
                                    ><span
                                        >来源第 {{ item.sourceVersionNo }} 版{{
                                            reused(item) ? ' · 沿用' : ''
                                        }}</span
                                    >
                                </footer>
                                <p v-if="item.suggestion">正式返修意见：{{ item.suggestion }}</p>
                                <button v-if="item.error" @click="access(item, 1)">
                                    刷新浏览授权
                                </button>
                            </article>
                        </div>
                        <nav class="pagination">
                            <span>第 {{ pageNo }} 页 · 共 {{ total }} 张</span
                            ><button :disabled="pageNo === 1" @click="load(pageNo - 1)">
                                上一页</button
                            ><button :disabled="pageNo * 6 >= total" @click="load(pageNo + 1)">
                                下一页
                            </button>
                        </nav>
                        <p v-if="!loading && !total">当前没有可读取的正式照片，请重试读取。</p>
                    </section>
                    <aside class="card summary">
                        <h2>{{ feedbackStep === 'selecting' ? '选择返修照片' : '精修交付' }}</h2>
                        <template v-if="canFeedback"
                            ><p v-if="feedbackStep === 'selecting'" class="count">
                                {{ feedbackPhotos.length }} 张
                            </p>
                            <p v-if="feedbackPhotos.length">
                                照片
                                {{ feedbackPhotos.map((i) => photoLabel(i.photoNo)).join('、') }}
                            </p>
                            <button
                                v-if="feedbackStep === 'selecting'"
                                class="primary"
                                :disabled="!feedbackPhotos.length || writeFrozen"
                                @click="feedbackStep = 'editing'"
                            >
                                填写返修备注 →</button
                            ><button
                                v-else
                                class="primary"
                                :disabled="writeFrozen"
                                @click="feedbackStep = 'selecting'"
                            >
                                选择返修照片
                            </button>
                            <hr />
                            <h3>照片满意，完成本次交付</h3>
                            <p>确认采用当前完整版本后，订单完成，不能继续返修或换片。</p>
                            <button :disabled="writeFrozen" @click="confirmDialog = true">
                                结束订单
                            </button></template
                        >
                        <p v-else>
                            {{
                                stage === 8
                                    ? '最终成片已就绪，可前往领取。'
                                    : paused
                                    ? '摄影争议暂停，正式操作暂不可用。'
                                    : viewVersionNo !== currentVersionNo
                                    ? '查看历史版本时不能反馈或采用。'
                                    : '当前阶段仅可查看正式照片与历史。'
                            }}
                        </p>
                        <p v-if="confirmation.unknown" role="alert">
                            确认结果待核实，请使用原确认请求。
                        </p>
                        <button v-if="confirmation.unknown" class="primary" @click="confirmFinal">
                            用原确认请求核验结果
                        </button>
                    </aside>
                </div>
                <section v-if="historyTotal" class="card history">
                    <details>
                        <summary>发布与正式意见历史 · {{ historyTotal }} 条</summary>
                        <article v-for="(event, index) in historyItems" :key="index">
                            <h3>
                                {{ event.operationName
                                }}<span v-if="event.targetVersionNo">
                                    · 第 {{ event.targetVersionNo }} 版</span
                                >
                                · {{ event.occurredAt }}
                            </h3>
                            <p v-if="event.deadlineAt">
                                期限代次 {{ event.deadlineRevision }} · 当前截止
                                {{ event.deadlineAt }} · 首次截止 {{ event.originalDeadlineAt }}
                            </p>
                            <p v-for="image in event.items" :key="image.retouchImageId">
                                #{{ image.photoNo }} · {{ image.suggestion || '无正式意见' }}
                                <span v-if="image.sourceVersionNo"
                                    >来源第 {{ image.sourceVersionNo }} 版{{
                                        image.reused ? ' · 沿用实际记录' : ''
                                    }}</span
                                >
                            </p>
                        </article>
                        <button :disabled="historyPage === 1" @click="loadHistory(historyPage - 1)">
                            上一页历史</button
                        ><button
                            :disabled="historyPage * 30 >= historyTotal"
                            @click="loadHistory(historyPage + 1)"
                        >
                            下一页历史
                        </button>
                    </details>
                </section>
                <div
                    v-if="feedbackSuccess"
                    class="retouch-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="返修需求已发送"
                >
                    <section class="card feedback-success">
                        <img src="@/assets/photography-delivery/27b5f.svg" alt="" />
                        <h2>返修需求已发送</h2>
                        <p>已经发送给摄影师，请您耐心等待。</p>
                        <hr />
                        <button class="primary" @click="feedbackSuccess = false">我知道了</button>
                    </section>
                </div>
                <div
                    v-if="confirmDialog"
                    class="retouch-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="确认最终采用"
                >
                    <section class="card confirmation">
                        <button
                            class="close-confirmation"
                            aria-label="关闭最终确认"
                            :disabled="confirmation.busy || confirmation.unknown"
                            @click="confirmDialog = false"
                        >
                            <img src="@/assets/photography-delivery/cd23d.svg" alt="" />
                        </button>
                        <h2>确认采用当前完整版本？</h2>
                        <p>
                            当前第
                            {{
                                currentVersionNo
                            }}
                            版包含全部已选照片。确认后订单完成，不能再提交返修意见。
                        </p>
                        <label
                            ><input
                                type="checkbox"
                                v-model="accepted"
                            />我确认接受当前完整精修结果</label
                        ><button
                            class="primary"
                            :disabled="
                                !accepted ||
                                confirmation.busy ||
                                feedbackState.busy ||
                                feedbackState.unknown
                            "
                            @click="confirmFinal"
                        >
                            {{
                                confirmation.unknown ? '用原确认请求核验结果' : '再次确认并完成订单'
                            }}</button
                        ><button
                            :disabled="confirmation.busy || confirmation.unknown"
                            @click="confirmDialog = false"
                        >
                            返回检查照片
                        </button>
                        <p role="alert">{{ confirmation.error }}</p>
                    </section>
                </div>
                <div
                    v-if="leaveAction"
                    class="retouch-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="离开返修流程"
                >
                    <section class="card confirmation">
                        <h2>尚有未提交内容，离开后将丢失</h2>
                        <p>取消离开可继续核对照片和返修意见。</p>
                        <button @click="leaveAction = null">继续编辑</button
                        ><button @click="confirmLeave">确认离开</button>
                    </section>
                </div>
                <div
                    v-if="active"
                    class="retouch-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="精修大图"
                >
                    <section class="card preview">
                        <button @click="active = null">关闭大图</button
                        ><img
                            v-if="active.url"
                            :src="active.url"
                            :alt="`精修#${active.photoNo}`"
                            @error="imageFailed(active)"
                        />
                        <p>#{{ active.photoNo }} · 来源第 {{ active.sourceVersionNo }} 版</p>
                        <p>首次建议：{{ active.initialSuggestion || '无' }}</p>
                        <p v-if="active.suggestion">正式返修意见：{{ active.suggestion }}</p>
                        <p v-if="active.error" role="alert">{{ active.error }}</p>
                        <button @click="access(active, 2)">刷新授权</button>
                    </section>
                </div>
            </main>
        </photography-delivery-shell>
        <photography-delivery-shell v-else :creator="true" :focused="creatorImport" :service-order-no="serviceOrderNo" :title="creatorTitle" @back="backToOrders">
        <main class="first-version creator-workspace">
            <p v-if="loading" role="status">正在读取正式交付范围…</p>
            <p v-if="error" role="alert">{{ error }}</p>
            <button v-if="!upload.files.length && !creatorImport || error" class="refresh-result" :disabled="upload.busy" @click="initialize">重新加载正式结果及授权</button>
            <p v-if="deadlineAt && !creatorImport" class="creator-deadline">
                {{ paused ? '暂停前阶段截止' : '有效阶段截止' }}：{{ deadlineAt }}
            </p>
            <section v-if="[4, 6, 8].includes(stage) && !paused" class="creator-result card">
                <h2>{{ stage === 8 ? '恭喜完成订单' : '精修片上传已成功' }}</h2>
                <div><span class="result-stack"><i></i><span><b><img src="@/assets/photography-delivery/e4911.svg" alt=""><em></em></b></span></span><article><h3>{{ stage === 8 ? '订单已完成' : '精修片已上传' }}</h3><p>{{ stage === 8 ? '已形成最终完整成片集合，感谢你的用心付出。' : '请摄影师耐心等待，用户可在七个连续自然日内确认或提交本轮返修。' }}</p><button class="primary" @click="backToOrders">查看订单详情</button></article></div>
                <p v-if="stage === 8">最终完成时间：{{ finalCompletedAt }}</p>
            </section>
            <p v-if="paused" role="status">
                摄影争议暂停，正式操作暂不可用。暂停开始：{{ pausedAt }}；解除后继续剩余有效时间。
            </p>
            <p v-else-if="deadlineExpired" role="status">
                {{
                    canContinueOverdueDelivery
                        ? '本阶段已逾期，平台已核实允许继续交付。'
                        : '本阶段期限已截止，请刷新最新处理状态。'
                }}
            </p>
            <section v-if="canFeedback">
                <h2>整轮反馈</h2>
                <p>对当前完整版本填写逐图意见，再核对整轮清单。正式提交后锁定，最多两轮返修。</p>
                <ul>
                    <li v-for="opinion in feedbackState.items" :key="opinion.retouchImageId">
                        #{{ opinion.photoNo }}：{{ opinion.suggestion }}
                    </li>
                </ul>
                <label
                    ><input
                        type="checkbox"
                        v-model="feedbackAccepted"
                        :disabled="feedbackState.busy || feedbackState.unknown"
                    />我已核对整轮照片和意见</label
                ><button
                    :disabled="
                        feedbackState.busy ||
                        confirmation.busy ||
                        confirmation.unknown ||
                        !feedbackAccepted
                    "
                    @click="submitFeedback"
                >
                    {{ feedbackState.unknown ? '用原反馈请求核验结果' : '再次确认并提交本轮意见' }}
                </button>
                <p role="alert">{{ feedbackState.error }}</p>
            </section>
            <section v-if="canFeedback">
                <h2>确认最终交付</h2>
                <p>请检查全部已选照片。接受后形成最终成片集合，正常流程不能更换图片。</p>
                <label
                    ><input
                        type="checkbox"
                        v-model="accepted"
                        :disabled="
                            confirmation.busy ||
                            confirmation.unknown ||
                            feedbackState.busy ||
                            feedbackState.unknown
                        "
                    />我确认接受当前完整精修结果</label
                ><button
                    :disabled="
                        confirmation.busy ||
                        feedbackState.busy ||
                        feedbackState.unknown ||
                        !accepted
                    "
                    @click="confirmFinal"
                >
                    {{ confirmation.unknown ? '用原确认请求核验结果' : '再次确认并完成订单' }}
                </button>
                <p role="alert">{{ confirmation.error }}</p>
            </section>
            <section v-if="stage === 8 && !creator">
                <h2>已完成 · 最终成片</h2>
                <router-link :to="{ name: 'photographyDownloads', params: { serviceOrderNo } }"
                    >领取最终 JPG 成片</router-link
                >
                <p>最终完成时间：{{ finalCompletedAt }}</p>
                <div class="grid">
                    <article v-for="item in finalItems" :key="item.finalImageId">
                        <button @click="preview(item)">
                            <img :src="item.url" :alt="`最终成片#${item.photoNo}`" />
                        </button>
                        <p>#{{ item.photoNo }} · 最终成片</p>
                        <p v-if="item.error" role="alert">{{ item.error }}</p>
                        <button @click="access(item, 1)">刷新成片浏览授权</button>
                    </article>
                </div>
                <button :disabled="finalPage === 1" @click="loadFinals(finalPage - 1)">
                    上一页成片</button
                ><button
                    :disabled="finalPage * 30 >= finalTotal"
                    @click="loadFinals(finalPage + 1)"
                >
                    下一页成片
                </button>
            </section>
            <section
                v-if="
                    creator &&
                    [3, 5, 7].includes(stage) &&
                    !paused &&
                    (!deadlineExpired || canContinueOverdueDelivery)
                "
            >
                <div v-if="creatorImport" class="retouch-import">
                    <h1>上传精修片</h1>
                    <div class="retouch-import-options">
                        <button :disabled="loading || !flow" @click="$refs.retouchFiles.click()"><span><img src="@/assets/photography-delivery/33398.svg" alt=""></span><strong>导入照片</strong></button>
                        <button :disabled="loading || !flow" @click="$refs.retouchFolder.click()"><span><img src="@/assets/photography-delivery/e5868.svg" alt=""></span><strong>导入文件夹</strong></button>
                    </div>
                    <p>JPG 每张不超过 30 MiB，单页合并 TIFF 不超过 200 MiB。导入后逐文件绑定并排核对。</p>
                    <details class="required-import"><summary>本轮正式交付范围 · {{ selected.length }} 张</summary><p v-for="sample in selected" :key="sample.id">#{{ sample.photoNo }} · {{ stage === 3 ? '首次建议' : '本轮正式返修意见' }}：{{ sample.suggestion || '无' }}</p></details>
                </div>
                <details v-if="!creatorImport" class="upload-contract"><summary>文件要求与交付规则</summary><p>
                    第一版交付全部正式已选照片，第二/三版仅交付本轮正式意见涉及的照片。JPG每张不超过30
                    MiB，单页合并 TIFF 不超过200
                    MiB；原件保持原始字节。TIFF需不透明底，CMYK需有效ICC。点击提交才上传；离开页面不保存未提交清单。
                </p></details>
                <input
                    ref="retouchFiles"
                    class="native-file-input"
                    type="file"
                    accept="image/jpeg,image/tiff,.jpg,.jpeg,.tif,.tiff"
                    multiple
                    :disabled="upload.busy || !!upload.submissionTicket"
                    @change="choose"
                />
                <input ref="retouchFolder" class="native-file-input" type="file" webkitdirectory multiple accept="image/jpeg,image/tiff,.jpg,.jpeg,.tif,.tiff" :disabled="upload.busy || !!upload.submissionTicket" @change="choose" />
                <p v-if="upload.error" role="alert">{{ upload.error }}</p>
                <div v-if="upload.files.length" class="retouch-upload-card">
                <header class="retouch-upload-heading"><h2>{{ creatorReview ? '逐文件绑定并排核对' : '精修照片' }} <small>共 {{ upload.files.length }} 张 · 第 {{ targetVersionNo }} 版</small></h2><button v-if="!upload.submissionTicket && !creatorReview" @click="$refs.retouchFiles.click()">重新导入</button></header>
                <p v-if="upload.submissionTicket" class="overall-progress">已上传 {{ uploadedFiles }} / {{ upload.files.length }} 张 · {{ uploadedPercent }}%<progress :value="uploadedBytes" :max="totalUploadBytes"></progress></p>
                <p v-if="(creatorReview || upload.submissionTicket) && pagedUploadFiles.length > 1" class="binding-scroll-hint">左右滑动查看其余文件对应 · 本页 {{ pagedUploadFiles.length }} 组</p>
                <div :class="['retouch-pairs', {reviewing: creatorReview || !!upload.submissionTicket}]">
                <div v-for="item in pagedUploadFiles" :key="item.clientFileId" class="pair">
                    <figure>
                        <img v-if="item.preview" :src="item.preview" :alt="item.name" />
                        <p v-else>
                            TIFF 本地无法预览，请按文件名与照片编号核对；提交后显示转换结果。
                        </p>
                        <figcaption><span>{{ item.name }}</span><b>{{ bound(item) ? photoLabel(bound(item).photoNo) : '待对应' }}</b></figcaption>
                    </figure>
                    <div>
                        <label
                            >明确对应样片<select
                                :value="item.sampleImageId"
                                :disabled="upload.busy || !!upload.submissionTicket"
                                @change="bind(item, $event.target.value)"
                            >
                                <option value="">请选择对应照片</option>
                                <option
                                    v-for="sample in selected"
                                    :key="sample.id"
                                    :value="sample.id"
                                >
                                    #{{ sample.photoNo }} {{ sample.originalFilename }}
                                </option>
                            </select></label
                        ><img
                            v-if="bound(item)"
                            :src="bound(item).url"
                            :alt="`样片#${bound(item).photoNo}`"
                        />
                        <p v-if="bound(item)" class="pair-opinion">
                            #{{ bound(item).photoNo }} 首次建议：{{
                                (stage === 3
                                    ? bound(item).suggestion
                                    : bound(item).initialSuggestion) || '无'
                            }}
                        </p>
                        <p v-if="bound(item) && stage !== 3" class="pair-opinion">
                            本轮正式返修意见：{{ bound(item).suggestion }}
                        </p>
                    </div>
                    <div v-if="upload.submissionTicket" class="file-processing">
                        <progress :value="item.bytes" :max="item.file.size"></progress>
                        <p>
                            原件：{{ node(item.technical.originalStatus) }} · 预览：{{
                                node(item.technical.previewStatus)
                            }}
                            · 缩略：{{ node(item.technical.thumbnailStatus) }}
                        </p>
                        <p role="alert" v-if="item.error">{{ item.error }}</p>
                        <button
                            v-if="
                                upload.submitted &&
                                (item.technical.retryable || item.technical.status === 4)
                            "
                            @click="flow.retry(item)"
                        >
                            用当前完整清单重试
                        </button>
                    </div>
                </div>
                </div>
                <nav class="upload-pages"><span>共 {{ upload.files.length }} 张 · 每页 12 张 · 第 {{ uploadPage }} 页</span><button :disabled="uploadPage === 1" @click="uploadPage--">上一页</button><button :disabled="uploadPage * 12 >= upload.files.length" @click="uploadPage++">下一页</button></nav>
                </div>
                <div v-if="upload.files.length" class="retouch-upload-actions">
                <button v-if="!creatorReview && !upload.submissionTicket" class="primary" @click="reviewUpload">核对对应与建议</button>
                <button v-if="creatorReview && !upload.submissionTicket" @click="creatorReview = false">返回调整文件</button>
                <button
                    v-if="creatorReview || upload.submissionTicket"
                    class="primary"
                    :disabled="
                        upload.busy || upload.published || upload.permanent || !upload.files.length
                    "
                    @click="submit"
                >
                    核对对应并提交完整第{{ targetVersionNo }}版</button
                ><button v-if="upload.submitAttempted && !upload.published" :disabled="upload.busy" @click="flow.recover()">
                    核验发布结果
                </button>
                <p role="status">
                    {{
                        upload.published
                            ? '本版已正式发布'
                            : upload.submitAttempted
                            ? '处理及发布结果待确认'
                            : '尚未正式提交'
                    }}
                </p>
                </div>
            </section>
            <section v-if="[3, 5, 7].includes(stage) && !creatorImport && !upload.files.length">
                <h2>正式已选范围（{{ selected.length }}张）</h2>
                <p v-for="sample in selected" :key="sample.id">
                    #{{ sample.photoNo }} · {{ stage === 3 ? '首次建议' : '本轮正式返修意见' }}：{{
                        sample.suggestion || '无'
                    }}
                </p>
            </section>
            <details v-if="stage >= 4 && !creatorImport && !upload.files.length" class="creator-records"><summary>查看完整版本与正式意见历史</summary>
            <section class="creator-version-card card">
                <h2>完整第{{ viewVersionNo }}版 · {{ total }}张</h2>
                <label
                    >查看已发布版本<select v-model.number="viewVersionNo" @change="load(1)">
                        <option v-for="version in currentVersionNo" :key="version" :value="version">
                            第{{ version }}版
                        </option>
                    </select></label
                >
                <div class="grid">
                    <article v-for="item in items" :key="item.retouchImageId">
                        <button @click="preview(item)">
                            <img :src="item.url" :alt="`精修#${item.photoNo}`" />
                        </button>
                        <p>#{{ item.photoNo }} · 来源第{{ item.sourceVersionNo }}版</p>
                        <p v-if="item.sourceVersionNo < viewVersionNo">
                            沿用第{{ item.sourceVersionNo }}版实际记录
                        </p>
                        <p>首次建议：{{ item.initialSuggestion || '无' }}</p>
                        <p>正式返修意见：{{ item.suggestion || '无' }}</p>
                        <label v-if="canFeedback"
                            >本轮意见<textarea
                                :value="
                                    (feedbackState.items.find(
                                        (i) => i.retouchImageId === item.retouchImageId
                                    ) &&
                                        feedbackState.items.find(
                                            (i) => i.retouchImageId === item.retouchImageId
                                        ).suggestion) ||
                                    ''
                                "
                                :disabled="feedbackState.busy || feedbackState.unknown"
                                @input="editFeedback(item, $event.target.value)"
                            ></textarea>
                        </label>
                        <p v-if="item.error" role="alert">{{ item.error }}</p>
                        <button @click="access(item, 1)">刷新浏览授权</button>
                    </article>
                </div>
                <button :disabled="pageNo === 1" @click="load(pageNo - 1)">上一页</button
                ><span>{{ pageNo }}</span
                ><button :disabled="pageNo * 30 >= total" @click="load(pageNo + 1)">下一页</button>
            </section>
            <section v-if="historyTotal" class="creator-history card">
                <h2>发布与正式意见历史</h2>
                <article v-for="(event, index) in historyItems" :key="index">
                    <h3>
                        {{ event.operationName
                        }}<template v-if="event.targetVersionNo > 0">
                            · 第{{ event.targetVersionNo }}版</template
                        >
                        · {{ event.occurredAt }}
                    </h3>
                    <p v-if="event.operationCode === 6 || event.operationCode === 7">
                        期限代次 {{ event.deadlineRevision }} · 当前截止 {{ event.deadlineAt }} ·
                        首次截止 {{ event.originalDeadlineAt
                        }}<span v-if="event.selectionCloseAt">
                            · 选片关闭 {{ event.selectionCloseAt }}</span
                        >
                    </p>
                    <p v-for="image in event.items" :key="image.retouchImageId">
                        #{{ image.photoNo }}
                        <span v-if="image.suggestion">正式意见：{{ image.suggestion }}</span
                        ><span v-if="image.sourceVersionNo > 0"
                            >来源第{{ image.sourceVersionNo }}版{{
                                image.reused ? ' · 沿用实际记录' : ''
                            }}</span
                        >
                    </p>
                </article>
                <button :disabled="historyPage === 1" @click="loadHistory(historyPage - 1)">
                    上一页历史</button
                ><button
                    :disabled="historyPage * 30 >= historyTotal"
                    @click="loadHistory(historyPage + 1)"
                >
                    下一页历史
                </button>
            </section>
            </details>
            <div
                v-if="active"
                class="overlay"
                role="dialog"
                aria-modal="true"
                aria-label="精修大图"
            >
                <section>
                    <button @click="active = null">关闭大图</button
                    ><img :src="active.url" :alt="`精修#${active.photoNo}`" />
                    <p>#{{ active.photoNo }} · 来源第{{ active.sourceVersionNo }}版</p>
                    <p>{{ active.error }}</p>
                    <button @click="access(active, 2)">刷新授权</button>
                </section>
            </div>
            <div v-if="leaveAction" class="overlay" role="dialog" aria-modal="true" aria-label="离开精修上传">
                <section class="card"><h2>尚有未提交内容，离开后将丢失</h2><p>未提交的本地文件和对应关系不会保存。</p><button @click="leaveAction = null">继续核对</button><button @click="confirmLeave">确认离开</button></section>
            </div>
        </main>
        </photography-delivery-shell>
    </div>
</template>
<script>
import request from '@/tool/request';
import API from '@/tool/api';
import samples from '@/tool/photographySamples.cjs';
import finals from '@/tool/photographyFinal.cjs';
import feedback from '@/tool/photographyFeedback.cjs';
import PhotographyDeliveryShell from '@/components/photography/PhotographyDeliveryShell.vue';
export default {
    components: { PhotographyDeliveryShell },
    props: { creator: { type: Boolean, default: false } },
    data() {
        return {
            feedbackSuccess: false,
            feedbackStep: 'browse',
            feedbackPhotos: [],
            confirmDialog: false,
            leaveAction: null,
            versionRead: 0,
            revision: 0,
            loading: false,
            error: '',
            stage: 0,
            paused: false,
            deadlineExpired: false,
            canContinueOverdueDelivery: false,
            deadlineAt: null,
            pausedAt: null,
            currentVersionNo: 0,
            viewVersionNo: 1,
            historyItems: [],
            historyPage: 1,
            historyTotal: 0,
            feedbackState: { items: [] },
            feedbackFlow: null,
            feedbackAccepted: false,
            accepted: false,
            confirmation: {},
            confirmFlow: null,
            confirmOrderNo: null,
            finalCompletedAt: null,
            finalItems: [],
            finalPage: 1,
            finalTotal: 0,
            selected: [],
            flow: null,
            upload: { files: [] },
            creatorReview: false,
            uploadPage: 1,
            items: [],
            total: 0,
            pageNo: 1,
            active: null,
        };
    },
    computed: {
        identityKey() {
            return this.$store && this.$store.state.user && this.$store.state.user.user_id;
        },
        creatorImport() {
            return this.creator && [3, 5, 7].includes(this.stage) && !this.paused && !this.upload.files.length;
        },
        creatorTitle() {
            return [3, 5, 7].includes(this.stage) ? (this.creatorReview ? '核对精修片' : '上传精修片') : this.stage === 8 ? '订单已完成' : '精修交付';
        },
        pagedUploadFiles() { return this.upload.files.slice((this.uploadPage - 1) * 12, this.uploadPage * 12); },
        uploadedFiles() { return this.upload.files.filter(i => i.ready).length; },
        uploadedBytes() { return this.upload.files.reduce((sum, i) => sum + Math.min(i.bytes, i.file.size), 0); },
        totalUploadBytes() { return this.upload.files.reduce((sum, i) => sum + i.file.size, 0); },
        uploadedPercent() { return this.totalUploadBytes ? Math.round(this.uploadedBytes / this.totalUploadBytes * 100) : 0; },
        workspaceTitle() {
            return this.feedbackStep === 'review'
                ? '核对本轮返修'
                : this.feedbackStep === 'editing'
                ? '告诉我们，哪里需要调整'
                : this.feedbackStep === 'selecting'
                ? '请选择返修照片'
                : '精修照片';
        },
        writeFrozen() {
            return !!(
                this.loading ||
                this.feedbackState.busy ||
                this.feedbackState.unknown ||
                this.confirmation.busy ||
                this.confirmation.unknown
            );
        },
        hasFeedbackDraft() {
            return !this.creator && !this.feedbackState.result && this.feedbackPhotos.length > 0;
        },
        hasUploadDraft() {
            return this.creator && this.upload.files.length > 0 && !this.upload.submissionTicket;
        },
        serviceOrderNo() {
            return this.$route.params.serviceOrderNo;
        },
        targetVersionNo() {
            return (this.stage - 1) / 2;
        },
        canFeedback() {
            return (
                !this.creator &&
                !(this.feedbackState && this.feedbackState.result) &&
                !(this.confirmation && this.confirmation.result) &&
                !this.paused &&
                !this.deadlineExpired &&
                [4, 6].includes(this.stage) &&
                [1, 2].includes(this.currentVersionNo) &&
                this.viewVersionNo === this.currentVersionNo
            );
        },
    },
    created() {
        this.initialize();
        if (typeof window !== 'undefined')
            window.addEventListener('beforeunload', this.beforeUnload);
    },
    beforeDestroy() {
        this.revision++;
        this.dispose();
        if (typeof window !== 'undefined')
            window.removeEventListener('beforeunload', this.beforeUnload);
    },
    beforeRouteUpdate(to, from, next) {
        if (to.params.serviceOrderNo !== from.params.serviceOrderNo && (this.hasFeedbackDraft || this.hasUploadDraft))
            this.leaveAction = () => next();
        else next();
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasFeedbackDraft || this.hasUploadDraft) this.leaveAction = () => next();
        else next();
    },
    watch: {
        identityKey() {
            this.resetContext();
            this.initialize();
        },
        serviceOrderNo() {
            this.resetContext();
            this.initialize();
        },
        'upload.published'(published) {
            if (published) this.initialize();
        },
    },
    methods: {
        reused(item) {
            return item.sourceVersionNo < this.viewVersionNo;
        },
        photoLabel(number) {
            return String(number).padStart(3, '0');
        },
        opinionValue(item) {
            const opinion = this.feedbackState.items.find(
                (i) => i.retouchImageId === String(item.retouchImageId)
            );
            return opinion ? opinion.suggestion : '';
        },
        isFeedbackPhoto(item) {
            return this.feedbackPhotos.some(
                (i) => String(i.retouchImageId) === String(item.retouchImageId)
            );
        },
        toggleFeedbackPhoto(item) {
            if (!this.canFeedback || this.writeFrozen) return;
            this.feedbackState.error = '';
            if (this.isFeedbackPhoto(item)) {
                this.feedbackPhotos = this.feedbackPhotos.filter(
                    (i) => String(i.retouchImageId) !== String(item.retouchImageId)
                );
                this.feedbackFlow.edit(item, '');
            } else this.feedbackPhotos.push({ ...item });
        },
        reviewFeedback() {
            if (!this.canFeedback || this.writeFrozen) return;
            if (
                !this.feedbackPhotos.length ||
                this.feedbackPhotos.some((i) => !this.opinionValue(i).trim())
            ) {
                this.feedbackState.error = '每张返修照片必须填写非空意见';
                return;
            }
            this.feedbackAccepted = true;
            this.feedbackStep = 'review';
        },
        async submitReviewedFeedback() {
            if (this.feedbackStep !== 'review') return;
            await this.submitFeedback();
            if (this.feedbackState.result) {
                this.feedbackStep = 'browse';
                this.feedbackPhotos = [];
                this.feedbackSuccess = true;
            }
        },
        beforeUnload(event) {
            if (this.hasFeedbackDraft || this.hasUploadDraft) {
                event.preventDefault();
                event.returnValue = '';
            }
        },
        backToOrders() {
            this.$router.push(
                this.creator
                    ? {
                          name: 'creatorServiceOrderDetail',
                          params: { serviceOrderNo: this.serviceOrderNo },
                      }
                    : { name: 'photographyAppointments' }
            );
        },
        confirmLeave() {
            const action = this.leaveAction;
            this.leaveAction = null;
            if (action) action();
        },
        resetContext() {
            this.creatorReview = false;
            this.uploadPage = 1;
            this.feedbackSuccess = false;
            this.feedbackFlow = null;
            this.confirmFlow = null;
            this.confirmOrderNo = null;
            this.feedbackState = { items: [] };
            this.confirmation = {};
            this.feedbackPhotos = [];
            this.feedbackStep = 'browse';
            this.confirmDialog = false;
            this.accepted = false;
            this.feedbackAccepted = false;
            this.historyItems = [];
            this.historyTotal = 0;
            this.finalItems = [];
            this.finalTotal = 0;
            this.active = null;
            this.leaveAction = null;
        },
        imageFailed(item) {
            item.url = '';
            item.error = '照片授权已失效或读取失败，编号与意见仍保留，请重新读取';
        },
        read(r) {
            const p = r.data || {};
            if (p.code !== 200 || p.success === false) throw new Error(p.message || '请求失败');
            return p.data !== undefined ? p.data : p.result;
        },
        dispose() {
            if (this.flow) this.flow.dispose();
            this.upload.files.forEach((i) => {
                if (i.preview) URL.revokeObjectURL(i.preview);
            });
            this.active = null;
        },
        async initialize() {
            const revision = ++this.revision;
            this.dispose();
            this.loading = true;
            this.error = '';
            this.stage = 0;
            this.canContinueOverdueDelivery = false;
            this.selected = [];
            this.items = [];
            this.upload = { files: [] };
            try {
                const result = this.read(
                    await request.get(
                        API.photography.sampleRetouch(this.serviceOrderNo, this.creator)
                    )
                );
                if (revision !== this.revision) return;
                if (
                    !this.creator &&
                    this.currentVersionNo &&
                    this.currentVersionNo !== result.currentVersionNo
                ) {
                    this.resetContext();
                }
                this.stage = result.stage;
                this.paused = result.paused;
                this.deadlineExpired = result.deadlineExpired === true;
                this.canContinueOverdueDelivery = result.canContinueOverdueDelivery === true;
                this.deadlineAt = result.deadlineAt;
                this.pausedAt = result.pausedAt;
                this.currentVersionNo = result.currentVersionNo;
                this.viewVersionNo = this.currentVersionNo || 1;
                if (!this.feedbackFlow || this.confirmOrderNo !== this.serviceOrderNo) {
                    this.feedbackFlow = feedback.createFeedback({
                        request,
                        API,
                        serviceOrderNo: this.serviceOrderNo,
                    });
                    this.feedbackState = this.feedbackFlow.state;
                    this.feedbackAccepted = false;
                }
                this.finalCompletedAt = result.finalCompletedAt;
                if (!this.confirmFlow || this.confirmOrderNo !== this.serviceOrderNo) {
                    this.confirmOrderNo = this.serviceOrderNo;
                    this.accepted = false;
                    this.confirmFlow = finals.createFinalConfirmation({
                        request,
                        API,
                        serviceOrderNo: this.serviceOrderNo,
                    });
                    this.confirmation = this.confirmFlow.state;
                }
                if (this.stage === 8 && !this.creator) await this.loadFinals(1);
                if (this.creator && [3, 5, 7].includes(this.stage)) {
                    let page = 1;
                    let total = 1;
                    const list = [];
                    while (list.length < total) {
                        const result = this.read(
                            await request.get(
                                this.stage === 3
                                    ? API.photography.samples(this.serviceOrderNo, true)
                                    : API.photography.versions(
                                          this.serviceOrderNo,
                                          true,
                                          this.currentVersionNo
                                      ),
                                { params: { pageNo: page++, pageSize: 100 } }
                            )
                        );
                        if (revision !== this.revision) return;
                        total = result.total;
                        if (!result.items.length && list.length < total)
                            throw new Error('正式样片分页不完整');
                        list.push(...result.items);
                    }
                    let required = null;
                    if (this.stage !== 3) {
                        const history = [];
                        let historyPage = 1, historyTotal = 1;
                        while (history.length < historyTotal) {
                            const result = this.read(await request.get(
                                API.photography.retouchHistory(this.serviceOrderNo, true),
                                { params: { pageNo: historyPage++, pageSize: 100 } }
                            ));
                            if (revision !== this.revision) return;
                            historyTotal = result.total;
                            if (!result.items.length && history.length < historyTotal)
                                throw new Error('本轮正式反馈记录不完整，暂不能上传');
                            history.push(...result.items);
                        }
                        const rounds = history.filter(event => event.operationCode === 4 &&
                            event.targetVersionNo === this.currentVersionNo);
                        if (rounds.length !== 1 || !rounds[0].items || !rounds[0].items.length)
                            throw new Error('本轮正式反馈范围无法核实，暂不能上传');
                        required = rounds[0].items;
                        if (required.some(opinion => !list.some(image =>
                            String(image.retouchImageId) === String(opinion.retouchImageId))) ||
                            new Set(required.map(i => String(i.retouchImageId))).size !== required.length)
                            throw new Error('本轮正式反馈与当前完整版本不一致，暂不能上传');
                    }
                    this.selected = list
                        .filter((i) => this.stage === 3 ? i.isSelected === 1 : required.some(
                            opinion => String(opinion.retouchImageId) === String(i.retouchImageId)))
                        .map((i) => ({
                            ...i,
                            suggestion: required ? required.find(opinion =>
                                String(opinion.retouchImageId) === String(i.retouchImageId)).suggestion : i.suggestion,
                            id: String(this.stage === 3 ? i.id : i.sampleImageId),
                            url: '',
                            error: '',
                        }));
                    this.flow = samples.createSampleUpload({
                        request,
                        API,
                        serviceOrderNo: this.serviceOrderNo,
                        retouch: true,
                        targetVersionNo: this.targetVersionNo,
                        selected: this.selected,
                    });
                    this.upload = this.flow.state;
                    let next = 0;
                    await Promise.all(
                        Array.from({ length: Math.min(3, this.selected.length) }, async () => {
                            while (next < this.selected.length) {
                                const item = this.selected[next++];
                                const r = this.read(
                                    await request.post(
                                        API.photography.sampleAccess(this.serviceOrderNo, true),
                                        {
                                            imageType: this.stage === 3 ? 1 : 2,
                                            imageId:
                                                this.stage === 3 ? item.id : item.retouchImageId,
                                            viewType: 2,
                                        }
                                    )
                                );
                                item.url = r.url;
                            }
                        })
                    );
                }
                if (this.currentVersionNo > 0) await this.load(1);
                await this.loadHistory(1);
            } catch (e) {
                if (revision === this.revision) this.error = e.message;
            } finally {
                if (revision === this.revision) this.loading = false;
            }
        },
        async loadHistory(page) {
            const revision = this.revision;
            try {
                const r = this.read(
                    await request.get(
                        API.photography.retouchHistory(this.serviceOrderNo, this.creator),
                        { params: { pageNo: page, pageSize: 30 } }
                    )
                );
                if (revision !== this.revision) return;
                this.historyItems = r.items;
                this.historyTotal = r.total;
                this.historyPage = page;
            } catch (e) {
                if (revision === this.revision) this.error = e.message;
            }
        },
        async confirmFinal() {
            if (!this.canFeedback) return;
            const revision = this.revision;
            if (
                await this.confirmFlow.submit(
                    {
                        stage: this.stage,
                        currentVersionNo: this.currentVersionNo,
                        paused: this.paused,
                    },
                    this.accepted
                )
            ) {
                if (revision !== this.revision) return;
                this.confirmDialog = false;
                this.feedbackPhotos = [];
                this.feedbackStep = 'browse';
                this.stage = 8;
                this.finalCompletedAt = this.confirmation.result.finalCompletedAt;
                await this.loadFinals(1);
            }
        },
        editFeedback(item, value) {
            try {
                this.feedbackFlow.edit(item, value);
                this.feedbackState.error = '';
            } catch (e) {
                this.feedbackState.error = e.message;
            }
        },
        async submitFeedback() {
            if (!this.canFeedback) return;
            const revision = this.revision;
            if (
                await this.feedbackFlow.submit(
                    {
                        stage: this.stage,
                        currentVersionNo: this.currentVersionNo,
                        paused: this.paused,
                    },
                    this.feedbackAccepted
                )
            ) {
                if (revision !== this.revision) return;
                this.stage = this.feedbackState.result.stage;
                this.feedbackAccepted = false;
                await this.load(1);
                await this.loadHistory(1);
            }
        },
        async loadFinals(page) {
            try {
                const r = this.read(
                    await request.get(API.photography.finals(this.serviceOrderNo), {
                        params: { pageNo: page, pageSize: 30 },
                    })
                );
                this.finalPage = page;
                this.finalTotal = r.total;
                this.finalItems = r.items.map((i) => ({ ...i, url: '', error: '' }));
                let next = 0;
                await Promise.all(
                    Array.from({ length: Math.min(3, this.finalItems.length) }, async () => {
                        while (next < this.finalItems.length)
                            await this.access(this.finalItems[next++], 1);
                    })
                );
            } catch (e) {
                this.error = e.message;
            }
        },
        choose(e) {
            try {
                const previous = this.upload.files;
                this.flow.select(e.target.files);
                previous.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
                this.creatorReview = false;
                this.uploadPage = 1;
                this.upload.files.forEach((i) =>
                    this.$set(
                        i,
                        'preview',
                        /\.tiff?$/i.test(i.name) ? '' : URL.createObjectURL(i.file)
                    )
                );
            } catch (error) {
                this.upload.error = error.message;
            }
            e.target.value = '';
        },
        bind(item, id) {
            try {
                this.flow.bind(item.clientFileId, id);
                this.upload.error = '';
            } catch (e) {
                this.upload.error = e.message;
            }
        },
        bound(item) {
            return this.selected.find((s) => s.id === item.sampleImageId);
        },
        reviewUpload() {
            try {
                this.flow.review();
                this.upload.error = '';
                this.creatorReview = true;
                this.uploadPage = 1;
            } catch (e) { this.upload.error = e.message; }
        },
        async submit() {
            await this.flow.submit();
        },
        node(v) {
            return { 0: '等待', 1: '处理中', 2: '就绪', 3: '失败', 4: '未知' }[v] || '等待';
        },
        async load(page) {
            const revision = this.revision,
                read = ++this.versionRead,
                version = this.viewVersionNo;
            try {
                const r = this.read(
                    await request.get(
                        API.photography.versions(
                            this.serviceOrderNo,
                            this.creator,
                            this.viewVersionNo
                        ),
                        { params: { pageNo: page, pageSize: this.creator ? 30 : 6 } }
                    )
                );
                if (
                    revision !== this.revision ||
                    read !== this.versionRead ||
                    version !== this.viewVersionNo
                )
                    return;
                this.pageNo = page;
                this.total = r.total;
                this.items = r.items.map((i) => ({ ...i, url: '', error: '' }));
                let next = 0;
                await Promise.all(
                    Array.from({ length: Math.min(3, this.items.length) }, async () => {
                        while (next < this.items.length) await this.access(this.items[next++], 1);
                    })
                );
            } catch (e) {
                if (revision === this.revision && read === this.versionRead) this.error = e.message;
            }
        },
        async access(item, viewType) {
            try {
                const r = this.read(
                    await request.post(
                        API.photography.sampleAccess(this.serviceOrderNo, this.creator),
                        { imageType: 2, imageId: item.retouchImageId, viewType }
                    )
                );
                item.url = r.url;
                item.error = '';
            } catch (e) {
                item.url = '';
                item.error = e.message;
            }
        },
        async preview(item) {
            this.active = { ...item, url: '' };
            await this.access(this.active, 2);
        },
    },
};
</script>
<style scoped>
.completed-review-context { display:flex; gap:24px; align-items:center; justify-content:space-between; padding:24px; margin:24px 0; border:1px solid #e9e8f1; border-radius:12px; background:#f8f6ff; }.completed-review-context h2 { margin:0 0 8px; font-size:18px; }.completed-review-context p { margin:0; color:#5e5e78; font-size:13px; }.completed-review-context a { flex-shrink:0; padding:12px 18px; border-radius:8px; background:#7c6ff0; color:#fff; text-decoration:none; }.completed-review-context a:focus-visible { outline:3px solid #6b5dd5; outline-offset:3px; }@media(max-width:600px) { .completed-review-context { flex-direction:column; align-items:stretch; padding:20px; }.completed-review-context a { text-align:center; } }

.retouch-workspace {
    line-height: 1.6;
    color: #292938;
    position: relative;
}
.subtitle {
    margin: 0 0 10px;
    color: #8e8e98;
}
.stage-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    font-size: 12px;
    color: #5e5e78;
    margin: 0 0 16px;
}
.refresh-progress {
    position: absolute;
    right: 0;
    top: -49px;
    font-size: 12px;
}
.retouch-workspace button,
.retouch-workspace select {
    font: inherit;
    color: #5e5e78;
    min-height: 40px;
    background: white;
    border: 1px solid #e9e8f1;
    border-radius: 8px;
    padding: 8px 16px;
    cursor: pointer;
}
.retouch-workspace button:disabled {
    cursor: default;
    opacity: 0.45;
}
.retouch-workspace button:focus-visible,
.retouch-workspace textarea:focus-visible,
.retouch-workspace select:focus-visible {
    outline: 3px solid #7b68ee;
    outline-offset: 2px;
}
.retouch-workspace .primary {
    display: block;
    box-sizing: border-box;
    text-align: center;
    background: #8068f6;
    color: white;
    border: 0;
    border-radius: 8px;
    text-decoration: none;
    padding: 10px 16px;
}
.workspace-columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 288px;
    gap: 24px;
    align-items: start;
}
.card {
    background: white;
    border: 1px solid #e9e8f1;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 3px 7px #211b4908;
    box-sizing: border-box;
}
.summary {
    padding: 24px;
}
.summary h2 {
    font-size: 16px;
    margin: 0 0 16px;
}
.summary h3 {
    font-size: 13px;
}
.summary p {
    font-size: 12px;
    color: #5e5e78;
    overflow-wrap: anywhere;
}
.summary > button {
    width: 100%;
    margin-top: 12px;
}
.summary .count {
    font-size: 40px;
    font-weight: 700;
    color: #292938;
    margin: 6px 0;
}
.summary hr {
    border: 0;
    border-top: 1px solid #e9e8f1;
    margin: 16px 0;
}
.badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 20px;
    background: #f4f0ff;
    color: #7b46ff;
    padding: 3px 8px;
    font-size: 12px;
}
.badge > img {
    flex-shrink: 0;
}
.gallery-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #e9e8f1;
    margin-bottom: 16px;
    padding-bottom: 16px;
    font-size: 12px;
}
.gallery-toolbar select {
    max-width: 100%;
    font-size: 12px;
}
.retouch-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}
.retouch-photo {
    position: relative;
    min-width: 0;
    border: 1px solid #e9e8f1;
    border-radius: 9px;
    overflow: hidden;
}
.retouch-photo.selected {
    border: 2px solid #947dff;
}
.retouch-photo .photo-open {
    padding: 0;
    border: 0;
    border-radius: 0;
    display: block;
    width: 100%;
    height: 236px;
    background: #f7f7fb;
    overflow: hidden;
}
.photo-open > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.retouch-photo .select-photo {
    position: absolute;
    right: 8px;
    top: 8px;
    padding: 4px;
    min-height: 26px;
    min-width: 26px;
    font-size: 11px;
    color: #7b46ff;
}
.empty-photo-check{display:block;width:22px;height:22px;border-radius:5px;background:#fffffff0;border:1px solid #e9e8f1}.retouch-photo .select-photo{display:grid;place-items:center;padding:0;border:0;background:transparent;min-width:24px;min-height:24px}.retouch-photo.selected .select-photo {
    background: #8068f6;
    color: white;
}
.retouch-photo footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    padding: 10px;
    font-size: 12px;
}
.retouch-photo footer span {
    font-size: 10px;
    color: #8e8e98;
}
.retouch-photo > p {
    font-size: 11px;
    color: #8e8e98;
    padding: 0 10px;
    margin: 0 0 8px;
    overflow-wrap: anywhere;
}
.pagination {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    font-size: 12px;
    color: #8e8e98;
}
.pagination > span {
    margin-right: auto;
}
.pagination > button {
    padding: 4px 8px;
    min-height: 32px;
}
.feedback-cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
}
.photo-note {
    padding: 20px;
    min-width: 0;
}
.photo-note > header {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 16px;
}
.photo-note strong {
    font-size: 16px;
}
.photo-note small {
    display: block;
    color: #8e8e98;
    font-size: 12px;
}
.photo-note > img {
    width: 100%;
    height: 300px;
    object-fit: cover;
    display: block;
    border-radius: 8px;
    margin-bottom: 16px;
}
.photo-note label {
    display: block;
    font-size: 13px;
    font-weight: 700;
}
.photo-note textarea,
.review-note {
    display: block;
    box-sizing: border-box;
    width: 100%;
    min-height: 146px;
    margin-top: 16px;
    border: 1px solid #e9e8f1;
    background: #f7f7fb;
    border-radius: 8px;
    padding: 12px;
    color: #5e5e78;
    font: 13px/20px 'Microsoft YaHei', sans-serif;
    resize: vertical;
    overflow-wrap: anywhere;
}
.photo-note > small {
    position: relative;
    margin-top: -28px;
    margin-left: 12px;
    pointer-events: none;
}
.photo-note textarea {
    padding-bottom: 34px;
}
.photo-note .filled {
    font-size: 12px;
    color: #16a34a;
    display: flex;
    gap: 6px;
    align-items: center;
    margin: 26px 0 0;
    min-height: 18px;
}
.notice {
    background: #f4f0ff;
    border-radius: 8px;
    padding: 16px;
    color: #5e5e78;
    font-size: 12px;
    display: flex;
    gap: 8px;
    align-items: center;
}
.notice > img {
    flex-shrink: 0;
}
.image-missing {
    display: flex;
    min-height: 160px;
    background: #f7f7fb;
    align-items: center;
    justify-content: center;
    padding: 12px;
    box-sizing: border-box;
    flex-direction: column;
    font-size: 12px;
    color: #8e8e98;
}
.history {
    margin-top: 16px;
    font-size: 12px;
}
.history summary {
    cursor: pointer;
    color: #5e5e78;
}
.history article {
    border-top: 1px solid #e9e8f1;
    margin-top: 16px;
}
.retouch-overlay {
    position: fixed;
    inset: 0;
    background: #16152299;
    z-index: 200;
    display: grid;
    place-items: center;
    padding: 20px;
    box-sizing: border-box;
}
.feedback-success {
    width: 520px;
    max-width: 100%;
    padding: 32px;
}
.feedback-success > h2 {
    font-size: 20px;
    margin: 24px 0;
}
.feedback-success > p {
    font-size: 13px;
    color: #5e5e78;
}
.feedback-success > hr {
    border: 0;
    border-top: 1px solid #e9e8f1;
    margin: 24px 0;
}
.feedback-success > button {
    width: 100%;
}
.confirmation {
    position: relative;
    max-width: 520px;
    padding: 32px;
    width: 100%;
}
.confirmation h2 {
    font-size: 22px;
    padding-right: 20px;
}
.confirmation .close-confirmation {
    position: absolute;
    right: 20px;
    top: 12px;
    padding: 0;
    border: 0;
    min-width: 40px;
    width: 40px;
    display: grid;
    place-items: center;
}
.confirmation label {
    display: block;
    font-size: 14px;
    margin: 20px 0;
}
.confirmation button {
    width: 100%;
    margin-top: 12px;
}
.preview {
    max-width: 90vw;
    max-height: 95vh;
    overflow: auto;
}
.preview > img {
    display: block;
    max-width: 80vw;
    max-height: 72vh;
    object-fit: contain;
}
.completion {
    display: flex;
    align-items: center;
    gap: 40px;
    background: white;
    border: 1px solid #e9e8f1;
    padding: 24px;
    border-radius: 12px;
    margin-bottom: 20px;
}
.completion > img {
    flex-shrink: 0;
}
.completion h2 {
    font-size: 28px;
    margin: 0 0 8px;
}
.completion p {
    font-size: 13px;
    color: #8e8e98;
}
.completion .primary {
    max-width: 260px;
}
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
@media (max-width: 1100px) {
    .workspace-columns {
        grid-template-columns: minmax(0, 1fr) 240px;
    }
    .card {
        padding: 16px;
    }
    .retouch-photo .photo-open {
        height: 200px;
    }
    .photo-note > img {
        height: 260px;
    }
}
@media (max-width: 800px) {
    .workspace-columns {
        grid-template-columns: minmax(0, 1fr);
    }
    .summary {
        order: -1;
    }
    .summary > button {
        max-width: 100%;
    }
    .summary .count {
        font-size: 28px;
    }
    .retouch-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .refresh-progress {
        position: static;
        margin-bottom: 12px;
    }
}
@media (max-width: 600px) {
    .completion {
        gap: 16px;
        padding: 16px;
    }
    .completion h2 {
        font-size: 20px;
    }
    .completion > img {
        display: none;
    }
    .feedback-cards {
        grid-template-columns: minmax(0, 1fr);
    }
    .retouch-photo .photo-open {
        height: 180px;
    }
    .retouch-grid {
        gap: 10px;
    }
    .gallery-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }
    .retouch-photo footer {
        flex-direction: column;
        align-items: flex-start;
    }
    .photo-note > img {
        height: 300px;
    }
    .preview {
        max-width: 100%;
        padding: 12px;
    }
    .preview > img {
        max-width: 100%;
        height: auto;
    }
    .subtitle {
        font-size: 12px;
    }
}

.first-version {
    max-width: 1200px;
    margin: 24px auto;
    padding: 24px;
    line-height: 1.6;
}
.first-version button,
.first-version select,
.first-version input {
    min-height: 44px;
    margin: 6px;
    padding: 8px;
}
.first-version :focus-visible {
    outline: 3px solid #684875;
}
.pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    border: 1px solid #ddd;
    padding: 16px;
    margin: 16px 0;
}
.pair figure {
    margin: 0;
}
.pair img,
.grid img {
    width: 100%;
    height: 240px;
    object-fit: contain;
}
.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
}
[role='alert'] {
    color: #a52e46;
}
.overlay {
    position: fixed;
    inset: 0;
    background: #000b;
    z-index: 200;
    display: grid;
    place-items: center;
}
.overlay section {
    background: white;
    padding: 20px;
}
.overlay img {
    display: block;
    max-width: 90vw;
    max-height: 75vh;
    object-fit: contain;
}
@media (max-width: 600px) {
    .pair {
        grid-template-columns: 1fr;
    }
    .first-version {
        padding: 12px;
        margin: 0;
    }
}
</style>
<style scoped>
.creator-workspace .upload-contract{position:absolute;right:0;top:-44px;margin:0;z-index:2;font-size:11px;max-width:350px}.upload-contract summary{cursor:pointer;text-align:right;color:#8e8e98}.upload-contract[open]{background:white;border:1px solid #e9e8f1;border-radius:8px;padding:12px;box-shadow:0 4px 16px #211b4914}.upload-contract p{color:#5e5e78;margin:8px 0 0}.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>img,.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>p{height:148px}.creator-workspace .retouch-pairs:not(.reviewing) .pair>div{padding:4px 8px}.creator-workspace .retouch-pairs:not(.reviewing) .pair label{font-size:10px}.creator-workspace .retouch-pairs:not(.reviewing) .pair select{min-height:28px;margin:2px 0 0;padding:2px;font-size:11px}.creator-workspace .retouch-pairs:not(.reviewing) .pair figcaption{padding:5px 8px;font-size:10px}.creator-workspace .retouch-pairs.reviewing{display:flex;overflow-x:auto;gap:16px;padding-bottom:16px;scroll-snap-type:x mandatory;scrollbar-color:#8068f6 #e9e8f1;scrollbar-width:thin}.creator-workspace .retouch-pairs.reviewing .pair{flex:0 0 calc(50% - 8px);min-width:420px;scroll-snap-align:start}.creator-workspace .reviewing .pair>div>img{margin-top:0}.required-import{margin-top:4px;color:#8e8e98;font-size:12px;max-width:680px}.required-import summary{cursor:pointer;text-align:center}.required-import p{font-size:12px;margin:4px 0}.retouch-import-options img{width:auto;height:auto;max-width:none}
@media(max-width:600px){.creator-workspace .upload-contract{position:static;max-width:none;margin:0 0 12px}.creator-workspace .retouch-pairs.reviewing .pair{flex-basis:100%;min-width:0}.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>img,.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>p{height:140px}.creator-workspace .reviewing .pair{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.creator-workspace .reviewing .pair-opinion{padding:8px;font-size:11px}}
</style>
<style scoped>
.creator-workspace{position:relative;max-width:none;margin:0;padding:0;color:#292938;font-size:13px;line-height:1.6}.creator-workspace button,.creator-workspace select{font:inherit;border:1px solid #e9e8f1;border-radius:8px;background:white;color:#5e5e78;padding:8px 14px;cursor:pointer;margin:0;min-height:40px}.creator-workspace button:disabled{opacity:.45;cursor:default}.creator-workspace .primary{background:#8068f6;color:white;border-color:#8068f6}.creator-workspace .refresh-result{font-size:11px;position:absolute;right:0;top:-50px;min-height:32px}.creator-deadline{font-size:12px;color:#8e8e98;margin:0 0 16px}.creator-workspace .native-file-input{position:absolute;width:1px;height:1px;opacity:0;overflow:hidden;pointer-events:none;padding:0;min-height:0}.retouch-import{min-height:calc(100vh - 140px);display:flex;flex-direction:column;justify-content:center;align-items:center;box-sizing:border-box;padding:32px}.retouch-import h1{font-size:28px;line-height:40px;margin:0 0 24px;font-weight:700}.retouch-import-options{display:flex;gap:24px;width:680px;max-width:100%}.creator-workspace .retouch-import-options button{flex:1;height:180px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;background:white;border-radius:12px}.retouch-import-options button>span{width:48px;height:48px;border-radius:12px;background:#f3f0ff;display:grid;place-items:center}.retouch-import>p{font-size:12px;color:#8e8e98;margin:20px 0 0;text-align:center}.upload-contract{font-size:11px;color:#8e8e98;margin:12px 0 20px;max-width:900px}.retouch-upload-card{background:#fff;border:1px solid #e9e8f1;border-radius:12px;padding:24px}.retouch-upload-heading{display:flex;align-items:center;gap:12px;justify-content:space-between;margin-bottom:16px}.retouch-upload-heading h2{font-size:16px;margin:0}.retouch-upload-heading small{font-size:12px;font-weight:400;color:#8e8e98;margin-left:10px}.creator-workspace .retouch-upload-heading button{color:#8068f6;background:#f3f0ff;border:0;min-height:28px;padding:2px 12px;font-size:12px}.retouch-pairs{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}.creator-workspace .pair{display:block;margin:0;padding:0;border:1px solid #e9e8f1;background:#fafafd;border-radius:8px;overflow:hidden;min-width:0}.pair>figure{margin:0}.creator-workspace .pair figure>img,.creator-workspace .pair figure>p{height:170px;width:100%;object-fit:contain;display:block;margin:0;box-sizing:border-box}.creator-workspace .pair figure>p{padding:40px 16px;color:#8e8e98;text-align:center;font-size:12px;display:flex;align-items:center}.pair figcaption{display:flex;align-items:center;justify-content:space-between;gap:8px;background:#fff;padding:8px 10px;font-size:11px;color:#5e5e78;border-top:1px solid #e9e8f1}.pair figcaption>span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.pair figcaption b{font-weight:400;white-space:nowrap;color:#8e8e98}.pair>div{padding:10px;background:white}.pair label{font-size:11px;color:#8e8e98;display:block}.creator-workspace .pair select{width:100%;margin:4px 0 8px;font-size:12px;padding:4px;min-height:32px}.retouch-pairs:not(.reviewing) .pair>div>img,.retouch-pairs:not(.reviewing) .pair-opinion{display:none}.retouch-pairs.reviewing{grid-template-columns:repeat(2,minmax(0,1fr))}.creator-workspace .reviewing .pair{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.creator-workspace .reviewing .pair figure>img,.creator-workspace .reviewing .pair figure>p,.creator-workspace .reviewing .pair>div>img{height:230px;width:100%;object-fit:contain}.reviewing .pair>figure{padding-top:53px}.reviewing .pair-opinion{background:#f7f7fb;border:1px solid #e9e8f1;border-radius:6px;padding:10px;font-size:12px;color:#5e5e78;white-space:pre-wrap;overflow-wrap:anywhere}.pair .file-processing{grid-column:1/-1;border-top:1px solid #e9e8f1;font-size:11px}.creator-workspace progress{width:100%;height:6px;accent-color:#8068f6;display:block}.overall-progress{font-size:12px;color:#8e8e98;margin:0 0 20px}.overall-progress progress{margin-top:8px}.upload-pages{display:flex;gap:8px;align-items:center;padding-top:20px;color:#8e8e98;font-size:11px}.upload-pages>span{margin-right:auto}.creator-workspace .upload-pages button{min-height:30px;padding:4px 10px}.retouch-upload-actions{display:flex;align-items:center;justify-content:flex-end;gap:12px;margin-top:20px;flex-wrap:wrap}.retouch-upload-actions>p{width:100%;text-align:right;font-size:12px;color:#8e8e98;margin:0}.creator-result,.creator-version-card,.creator-history{margin-top:24px}.creator-result h2{font-size:16px;margin:0 0 28px}.creator-result>div{display:flex;gap:24px;align-items:center;max-width:650px;margin:32px auto}.creator-result>div>img{flex-shrink:0}.creator-result h3{font-size:16px;margin:0 0 8px}.creator-result p{color:#8e8e98;font-size:12px}.creator-result article{flex:1}.creator-result article button{width:100%;margin-top:12px}.creator-workspace .grid article{background:#fafafd;border:1px solid #e9e8f1;border-radius:8px;padding:12px}.creator-workspace .grid p{font-size:12px;color:#5e5e78}.creator-workspace .grid button{max-width:100%}.creator-workspace .overlay{background:#29293866;padding:20px}.creator-workspace .overlay section{max-width:520px;border-radius:16px;width:100%;box-sizing:border-box;padding:32px}.creator-workspace .overlay button{margin-right:12px}.creator-workspace .overlay h2{font-size:20px;margin:0 0 16px}.creator-workspace .overlay p{color:#8e8e98;margin-bottom:24px}
@media(max-width:1100px){.retouch-pairs{grid-template-columns:repeat(3,minmax(0,1fr))}.retouch-pairs.reviewing{grid-template-columns:minmax(0,1fr)}}@media(max-width:600px){.creator-workspace{padding:0;margin:0}.retouch-import{min-height:calc(100vh - 100px);padding:20px}.retouch-import-options{gap:12px}.creator-workspace .retouch-import-options button{height:160px;padding:12px}.retouch-import h1{font-size:24px}.retouch-upload-card{padding:12px}.retouch-pairs{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.creator-workspace .pair figure>img,.creator-workspace .pair figure>p{height:140px}.retouch-upload-heading{align-items:flex-start}.retouch-upload-heading small{display:block;margin:4px 0 0}.creator-workspace .reviewing .pair figure>img,.creator-workspace .reviewing .pair figure>p,.creator-workspace .reviewing .pair>div>img{height:180px}.reviewing .pair>div{padding:8px}.upload-pages{flex-wrap:wrap}.upload-pages>span{flex-basis:100%}.creator-workspace .refresh-result{position:static;margin-bottom:12px}.retouch-upload-actions>button{flex:1;min-width:140px}.creator-result>div{gap:12px}.creator-workspace .grid{grid-template-columns:minmax(0,1fr)}}

.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>img,.creator-workspace .retouch-pairs:not(.reviewing) .pair figure>p{height:116px}.creator-workspace .retouch-pairs:not(.reviewing) .pair figcaption{padding:3px 8px;line-height:18px}.creator-workspace .retouch-pairs:not(.reviewing) .pair>div{padding:2px 8px}.creator-workspace .retouch-pairs:not(.reviewing) .pair select{height:26px;min-height:26px}.creator-records{margin-top:24px;font-size:13px;color:#5e5e78}.creator-records>summary{cursor:pointer;background:white;border:1px solid #e9e8f1;border-radius:8px;padding:14px 20px}.reviewing .pair figcaption>span{white-space:normal;overflow-wrap:anywhere}.creator-workspace .creator-result{min-height:360px}.creator-workspace .creator-result>div{margin:64px auto}.creator-workspace .creator-result h2{font-weight:700}

.result-stack{position:relative;display:block;flex-shrink:0;width:40px;height:44px}.result-stack>i{position:absolute;left:5.2px;top:2.8px;width:27px;height:34px;border-radius:3px;background:#d5d1f6;transform:rotate(-12deg)}.result-stack>span{position:absolute;left:3.52px;top:3px;width:33.13px;height:38.949px;display:flex;align-items:center;justify-content:center}.result-stack b{box-sizing:border-box;display:flex;flex-direction:column;gap:3px;align-items:flex-start;width:28px;height:35px;background:white;border:1px solid #e9e8f1;border-radius:3px;padding:3px;overflow:hidden;transform:rotate(9deg)}.result-stack img{width:auto;height:auto;max-width:none;flex-shrink:0}.result-stack em{display:block;flex-shrink:0;width:12px;height:2px;background:#dbd8ef}

.binding-scroll-hint{margin:0 0 12px;color:#8e8e98;font-size:12px}
</style>
